import { FC, useMemo } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { TbTable } from 'react-icons/tb';

import AnalyticsSectionCard from '@/shared/components/cards/AnalyticsSectionCard';
import { EnrichedPill } from '@/shared/components/enriched-pills';
import { CompactActiveAvatarGroup } from '@/shared/components/groups';
import type { CompactActiveAvatarCollaborator } from '@/shared/components/groups';
import EmptyView from '@/shared/components/layouts/views/EmptySection';
import PlatformTable from '@/shared/components/tables/PlatformTable';
import { WorkspaceResourceAvatar, WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';
import { getWorkspaceResourceTypeLabel } from '@/shared/components/workspace-resources/resourceLabel';
import { useMemberStore } from '@/stores';
import type { Member } from '@/types';
import { formatDate } from '@/utilities';

import { ResourceUsage } from '../../types';
import { UsageTableSkeleton } from '../UsageAnalyticsSkeletons';

interface UsageResourcesTableProps {
    loading?: boolean;
    resources: ResourceUsage[];
}

const RESOURCE_ACTIVITY_COLUMN_WIDTHS = {
    collaborators: 180,
    createdAt: 180,
    lastActivityAt: 200,
    resource: 320,
    resourceType: 220,
    totalEvents: 160,
} as const;
const RESOURCE_ACTIVITY_TABLE_MIN_WIDTH = Object.values(RESOURCE_ACTIVITY_COLUMN_WIDTHS).reduce(
    (total, width) => total + width,
    0,
);

const resourceActivityCollator = new Intl.Collator('en-US', {
    numeric: true,
    sensitivity: 'base',
});

const compareText = (left: string, right: string): number => resourceActivityCollator.compare(left, right);

const resourceDisplayLabel = (resource: ResourceUsage): string => resource.resourceName?.trim() || resource.resourceId;

const compareTimestamp = (left: string, right: string): number => {
    const leftTimestamp = Date.parse(left);
    const rightTimestamp = Date.parse(right);

    return (
        (Number.isNaN(leftTimestamp) ? Number.NEGATIVE_INFINITY : leftTimestamp) -
        (Number.isNaN(rightTimestamp) ? Number.NEGATIVE_INFINITY : rightTimestamp)
    );
};

const uniqueMemberIds = (memberIds: readonly string[]): string[] => [
    ...new Set(memberIds.map((memberId) => memberId.trim()).filter(Boolean)),
];

const memberDisplayName = (member: Member): string => {
    const fullName = [member.user.firstName, member.user.lastName].filter(Boolean).join(' ').trim();

    return fullName || member.user.emailAddress || member.id;
};

const UsageResourcesTable: FC<UsageResourcesTableProps> = ({ loading = false, resources }): JSX.Element => {
    const members = useMemberStore((state) => state.members);
    const membersLoading = useMemberStore((state) => state.loading);
    const memberByIdentifier = useMemo(() => {
        const identifiers = new Map<string, Member>();

        members.forEach((member) => {
            identifiers.set(member.id, member);
            identifiers.set(member.user.id, member);
        });

        return identifiers;
    }, [members]);
    const columns = useMemo<ColumnsType<ResourceUsage>>(() => {
        const resolveCollaborators = (memberIds: readonly string[]): CompactActiveAvatarCollaborator[] =>
            uniqueMemberIds(memberIds).map((memberId) => {
                const member = memberByIdentifier.get(memberId);

                return member ?? { id: memberId, user: { id: memberId } };
            });
        const collaboratorSortLabel = (memberIds: readonly string[]): string =>
            uniqueMemberIds(memberIds)
                .map((memberId) => {
                    const member = memberByIdentifier.get(memberId);

                    return member ? memberDisplayName(member) : memberId;
                })
                .sort(compareText)
                .join('|');

        return [
            {
                title: 'Resource',
                dataIndex: 'resourceId',
                key: 'resource',
                fixed: 'left',
                width: RESOURCE_ACTIVITY_COLUMN_WIDTHS.resource,
                render: (value: string, resource) => (
                    <EnrichedPill interactive={false} style={{ margin: 0, maxWidth: '16rem' }}>
                        <WorkspaceResourceAvatar resource={resource.resourceType} size={18} />
                        <span
                            title={resource.resourceName ? `${resource.resourceName} (${value})` : value}
                            style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {resourceDisplayLabel(resource)}
                        </span>
                    </EnrichedPill>
                ),
                sorter: (left, right) =>
                    compareText(resourceDisplayLabel(left), resourceDisplayLabel(right)) ||
                    compareText(left.resourceId, right.resourceId),
            },
            {
                title: 'Workspace Resource',
                dataIndex: 'resourceType',
                key: 'workspaceResource',
                width: RESOURCE_ACTIVITY_COLUMN_WIDTHS.resourceType,
                render: (value: string) => (
                    <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem' }}>
                        <span aria-hidden='true' style={{ display: 'inline-flex', lineHeight: 0 }}>
                            <WorkspaceResourceGlyph resource={value} size='1rem' />
                        </span>
                        <span>{getWorkspaceResourceTypeLabel(value, { context: 'global' })}</span>
                    </span>
                ),
                sorter: (left, right) =>
                    compareText(
                        getWorkspaceResourceTypeLabel(left.resourceType, { context: 'global' }),
                        getWorkspaceResourceTypeLabel(right.resourceType, { context: 'global' }),
                    ),
            },
            {
                title: 'Collaborators',
                dataIndex: 'membersInvolved',
                key: 'collaborators',
                width: RESOURCE_ACTIVITY_COLUMN_WIDTHS.collaborators,
                render: (memberIds: string[]) => {
                    const collaborators = resolveCollaborators(memberIds);

                    if (!membersLoading && collaborators.length === 0) {
                        return <span aria-label='No collaborators'>-</span>;
                    }

                    return (
                        <CompactActiveAvatarGroup
                            collaborators={collaborators}
                            loading={membersLoading}
                            maxCount={4}
                            size={20}
                        />
                    );
                },
                sorter: (left, right) => {
                    const countDifference =
                        uniqueMemberIds(left.membersInvolved).length - uniqueMemberIds(right.membersInvolved).length;

                    return (
                        countDifference ||
                        compareText(
                            collaboratorSortLabel(left.membersInvolved),
                            collaboratorSortLabel(right.membersInvolved),
                        )
                    );
                },
            },
            {
                title: 'Total Events',
                dataIndex: 'totalEvents',
                width: RESOURCE_ACTIVITY_COLUMN_WIDTHS.totalEvents,
                sorter: (left, right) => left.totalEvents - right.totalEvents,
            },
            {
                title: 'Created At',
                dataIndex: 'createdAt',
                render: (value: string) => <>{value && formatDate(new Date(value))}</>,
                sorter: (left, right) => compareTimestamp(left.createdAt, right.createdAt),
                width: RESOURCE_ACTIVITY_COLUMN_WIDTHS.createdAt,
            },
            {
                title: 'Last Updated At',
                dataIndex: 'lastActivityAt',
                defaultSortOrder: 'descend',
                render: (value: string) => <>{value && formatDate(new Date(value))}</>,
                sorter: (left, right) => compareTimestamp(left.lastActivityAt, right.lastActivityAt),
                width: RESOURCE_ACTIVITY_COLUMN_WIDTHS.lastActivityAt,
            },
        ];
    }, [memberByIdentifier, membersLoading]);

    return (
        <AnalyticsSectionCard
            ariaLabel='Workspace resource activity'
            contentStyle={{ padding: '0.75rem' }}
            description='Inspect event volume and recent activity for each workspace resource.'
            icon={<TbTable />}
            title='Resource Activity'
        >
            {loading ? (
                <UsageTableSkeleton />
            ) : resources.length ? (
                <PlatformTable
                    columns={columns}
                    dataSource={resources}
                    rowKey={(resource) => `${resource.resourceType}:${resource.resourceId}`}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    size='small'
                    bordered
                    scroll={{ x: RESOURCE_ACTIVITY_TABLE_MIN_WIDTH }}
                />
            ) : (
                <EmptyView
                    description='Workspace resources will appear here after activity is recorded.'
                    height='13rem'
                    icon={<TbTable size='1.25rem' />}
                    label='No Resource Activity'
                />
            )}
        </AnalyticsSectionCard>
    );
};

export default UsageResourcesTable;
